import React, { useState, useContext } from 'react';
import { Header, Button, Grid, Table } from '@amzn/awsui-components-core';
import { Owner } from '@amzn/awscat-pattern-library-service-model';

import UserIdAutosuggest, {
  AdditionlUserInfo,
  SearchUserProfile,
} from '../common/UserIdAutosuggest';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { AppContext } from '../../contexts/AppContext';
import constants from '../../common/constants';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';

interface Props {
  collectionId: string;
  owners: Owner[];
  setOwners: (owners: Owner[]) => void;
  isAdminTable?: boolean;
}

const EditCollectionOwners = ({ collectionId, owners, setOwners, isAdminTable }: Props) => {
  const appContext = useContext(AppContext);
  const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();
  const userProfile = appContext?.authnInfo?.userProfile;

  const [selectedUser, setSelectedUser] = useState<SearchUserProfile>();
  const [disableAddOwner, setDisableAddOwner] = useState<boolean>(true);
  const [isAddingOwner, setIsAddingOwner] = useState<boolean>(false);
  const [isDeletingOwner, setIsDeletingOwner] = useState<boolean>(false);
  const [deletingOwnerId, setDeletingOwnerId] = useState<string>('');

  const handleUserSelected = (
    selectedUserId: string,
    selectedUserName: string,
    validationError: string,
    additionalInfo: AdditionlUserInfo,
  ) => {
    setSelectedUser({
      userId: selectedUserId,
      name: selectedUserName,
      email: additionalInfo?.email || '',
    });
    if (validationError || selectedUserName.includes('Partner')) {
      setDisableAddOwner(true);
    } else {
      setDisableAddOwner(false);
    }
  };

  const { items, collectionProps } = useCollection(owners, { sorting: {} });

  const handleDeleteOwner = async (userId: string) => {
    setDeletingOwnerId(userId);
    setIsDeletingOwner(true);
    const response = await mplServiceClient.removeCollectionOwner(collectionId, userId);
    if (setOwners) {
      setOwners(response.owners);
    }
    setDeletingOwnerId('');
    setIsDeletingOwner(false);
  };

  const renderDeleteIcon = (owner: Owner) => {
    if (owner.userId === userProfile?.userId && !isAdminTable) {
      return null;
    }
    const isLoading = isDeletingOwner && owner.userId === deletingOwnerId;
    return (
      <Button
        loading={isLoading}
        variant="icon"
        iconName="remove"
        ariaLabel="Remove owner"
        onClick={() => handleDeleteOwner(owner.userId)}
      />
    );
  };

  const columnDefinitions = [
    {
      id: constants.EDIT_COLLECTION_COL_LABEL_NAME,
      header: constants.EDIT_COLLECTION_COL_LABEL_NAME,
      // @ts-expect-error TS7006 - strict migration
      cell: (owner) => owner.name,
      sortingField: 'name',
    },
    {
      id: constants.EDIT_COLLECTION_COL_LABEL_EMAIL,
      header: constants.EDIT_COLLECTION_COL_LABEL_EMAIL,
      // @ts-expect-error TS7006 - strict migration
      cell: (owner) => owner.email,
      sortingField: 'email ',
    },
    {
      id: constants.EDIT_COLLECTION_COL_LABEL_DELETE,
      header: '',
      // @ts-expect-error TS7006 - strict migration
      cell: (owner) => renderDeleteIcon(owner),
      width: 70,
    },
  ];

  const renderActions = () => {
    return (
      <Button onClick={handleAddAuthor} disabled={disableAddOwner} loading={isAddingOwner}>
        {constants.EDIT_COLLECTION_PERMISSION_LABEL_ADD_OWNER}
      </Button>
    );
  };

  const renderHeader = () => {
    return (
      <Header variant="h3" counter={`(${owners.length})`}>
        Owners
      </Header>
    );
  };

  const handleAddAuthor = async () => {
    const { userId } = selectedUser ?? {};
    if (!userId || owners.some((owner) => owner.userId.includes(userId))) {
      setDisableAddOwner(true);
      return;
    } else {
      setIsAddingOwner(true);
      const response = await mplServiceClient.addCollectionOwner(collectionId, userId);
      if (setOwners) {
        setOwners(response.owners);
      }
      setIsAddingOwner(false);
      setDisableAddOwner(true);
    }
  };

  const renderFiltering = () => {
    return (
      <Grid gridDefinition={[{ colspan: 5 }, { colspan: 7 }]}>
        <UserIdAutosuggest onUserSelected={handleUserSelected} />
        <Button onClick={handleAddAuthor} disabled={disableAddOwner} loading={isAddingOwner}>
          {constants.EDIT_COLLECTION_PERMISSION_LABEL_ADD_OWNER}
        </Button>
      </Grid>
    );
  };

  return (
    <div className="awsui">
      <Table
        {...collectionProps}
        columnDefinitions={columnDefinitions}
        filter={renderFiltering()}
        items={items}
        header={renderHeader()}
      />
    </div>
  );
};

export default EditCollectionOwners;
